import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock,
  Activity,
  Globe,
  Code,
  Eye,
  RefreshCw,
  Wifi,
  WifiOff
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
// WebSocket disabled for laptop deployment

interface ComplianceMetrics {
  timestamp: string;
  domain: string;
  overallCompliance: {
    score: number;
    status: 'compliant' | 'partial' | 'non-compliant';
    lastUpdated: string;
  };
  securityHeaders: {
    csp: {
      present: boolean;
      score: number;
      issues: number;
      criticalIssues: number;
    };
    hsts: string;
    frameOptions: string;
    contentType: string;
  };
  javascriptIntegrity: {
    totalScripts: number;
    inlineScripts: number;
    thirdPartyScripts: number;
    highEntropyScripts: number;
    averageEntropy: string;
  };
  networkCompliance: {
    httpsRatio: number;
    thirdPartyDomains: number;
    suspiciousDomains: number;
  };
  alerts: {
    active: number;
    critical: number;
    warnings: number;
    info: number;
  };
  trends: {
    complianceChange: number;
    newVulnerabilities: number;
    resolvedIssues: number;
  };
}

export default function RealTimeCompliance() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentDomain, setCurrentDomain] = useState<string>('');
  // No demo mode - only real data
  const queryClient = useQueryClient();

  // Use HTTP polling for compliance monitoring (WebSocket disabled)
  const { data: complianceData, isLoading, error } = useQuery<ComplianceMetrics>({
    queryKey: ["/api/compliance/real-time"],
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Debug logging
  console.log('🔍 Compliance page debug:', { complianceData, isLoading, error });

  // No WebSocket subscription needed - using HTTP polling

  // Set current domain from compliance data
  useEffect(() => {
    if (complianceData?.domain && !currentDomain) {
      setCurrentDomain(complianceData.domain);
    }
  }, [complianceData, currentDomain]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ["/api/compliance/real-time"] });
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'text-purple-600 bg-green-50';
      case 'partial': return 'text-yellow-600 bg-yellow-50';
      case 'non-compliant': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getHeaderStatus = (status: string) => {
    return status === 'present' ? (
      <Badge variant="secondary" className="bg-green-50 text-purple-600">
        <CheckCircle className="w-3 h-3 mr-1" />
        Present
      </Badge>
    ) : (
      <Badge variant="destructive">
        <XCircle className="w-3 h-3 mr-1" />
        Missing
      </Badge>
    );
  };

  if (isLoading) {
  return (
    <div className="space-y-6 w-full max-w-none">
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Activity className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">Loading real-time compliance data...</p>
        </div>
      </div>
    </div>
  );
  }

  if (error || !complianceData || (complianceData as any)?.status === 'no-data') {
    return (
      <div className="space-y-6 w-full max-w-none">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Compliance Data Available</h3>
            <p className="text-gray-600 mb-4">
              {(complianceData as any)?.message || "No scan data available for compliance monitoring"}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              To enable real-time compliance monitoring, you need to run a security scan first.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => window.location.href = '/domains'} 
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Shield className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
              <Button 
                onClick={handleRefresh} 
                disabled={isRefreshing}
                variant="outline"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Retry
              </Button>
            </div>
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">How to get started:</h4>
              <ol className="text-sm text-blue-800 dark:text-blue-200 text-left max-w-md mx-auto">
                <li className="mb-1">1. Go to the Dashboard</li>
                <li className="mb-1">2. Click "Start New Scan"</li>
                <li className="mb-1">3. Enter your website URL</li>
                <li className="mb-1">4. Wait for scan completion</li>
                <li className="mb-1">5. Return here for real-time monitoring</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-none">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Real-Time Compliance Monitor</h1>
            {/* Real-time connection status */}
            <Badge variant="secondary" className="bg-green-50 text-green-600">
              <Wifi className="w-3 h-3 mr-1" />
              Live
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">
            Live security and compliance monitoring for {complianceData.domain}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="text-xs">
            <Clock className="w-3 h-3 mr-1" />
            Last updated: {new Date(complianceData.timestamp).toLocaleTimeString()}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Compliance Score */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2 text-purple-600" />
            Overall Compliance Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {complianceData.overallCompliance.score}/100
              </div>
              <Badge className={getStatusColor(complianceData.overallCompliance.status)}>
                {complianceData.overallCompliance.status.toUpperCase()}
              </Badge>
              <div className="mt-2 text-sm text-gray-600">
                {complianceData.overallCompliance.score >= 80 ? 'Excellent security posture' :
                 complianceData.overallCompliance.score >= 60 ? 'Good security with room for improvement' :
                 complianceData.overallCompliance.score >= 40 ? 'Moderate security concerns detected' :
                 'Significant security improvements needed'}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="relative">
                <Progress 
                  value={complianceData.overallCompliance.score} 
                  className={`w-full h-4 mb-4 ${
                    complianceData.overallCompliance.score >= 80 ? '[&>div]:bg-green-500' :
                    complianceData.overallCompliance.score >= 60 ? '[&>div]:bg-yellow-500' :
                    complianceData.overallCompliance.score >= 40 ? '[&>div]:bg-orange-500' :
                    '[&>div]:bg-red-500'
                  }`}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0</span>
                  <span>25</span>
                  <span>50</span>
                  <span>75</span>
                  <span>100</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Domain:</span>
                  <div className="font-medium">{complianceData.domain}</div>
                </div>
                <div>
                  <span className="text-gray-600">Last Scan:</span>
                  <div className="font-medium">
                    {new Date(complianceData.overallCompliance.lastUpdated).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Security Headers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              Security Headers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600 mb-1">Content Security Policy</div>
                {complianceData.securityHeaders.csp.present ? (
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="bg-green-50 text-purple-600">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Present
                    </Badge>
                    <span className="text-sm">
                      Score: {complianceData.securityHeaders.csp.score}/100
                    </span>
                  </div>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="w-3 h-3 mr-1" />
                    Missing
                  </Badge>
                )}
                {complianceData.securityHeaders.csp.issues > 0 && (
                  <div className="text-xs text-yellow-600 mt-1">
                    {complianceData.securityHeaders.csp.issues} issues found
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">HSTS</div>
                {getHeaderStatus(complianceData.securityHeaders.hsts)}
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">X-Frame-Options</div>
                {getHeaderStatus(complianceData.securityHeaders.frameOptions)}
              </div>
              <div>
                <div className="text-sm text-gray-600 mb-1">X-Content-Type-Options</div>
                {getHeaderStatus(complianceData.securityHeaders.contentType)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* JavaScript Integrity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Code className="w-5 h-5 mr-2 text-purple-600" />
              JavaScript Integrity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Total Scripts</div>
                <div className="text-2xl font-bold">
                  {complianceData.javascriptIntegrity.totalScripts}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Inline Scripts</div>
                <div className="text-2xl font-bold text-yellow-600">
                  {complianceData.javascriptIntegrity.inlineScripts}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Third-Party Scripts</div>
                <div className="text-2xl font-bold text-red-600">
                  {complianceData.javascriptIntegrity.thirdPartyScripts}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Average Entropy</div>
                <div className="text-2xl font-bold">
                  {complianceData.javascriptIntegrity.averageEntropy}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Network Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Globe className="w-5 h-5 mr-2 text-purple-600" />
              Network Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">HTTPS Ratio</div>
                <div className="text-2xl font-bold">
                  {complianceData.networkCompliance.httpsRatio.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Third-Party Domains</div>
                <div className="text-2xl font-bold text-orange-600">
                  {complianceData.networkCompliance.thirdPartyDomains}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
              Active Security Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Total Active</div>
                <div className="text-2xl font-bold">
                  {complianceData.alerts.active}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Critical</div>
                <div className="text-2xl font-bold text-red-600">
                  {complianceData.alerts.critical}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Warnings</div>
                <div className="text-2xl font-bold text-yellow-600">
                  {complianceData.alerts.warnings}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Info</div>
                <div className="text-2xl font-bold text-blue-600">
                  {complianceData.alerts.info}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="w-5 h-5 mr-2 text-indigo-600" />
            Compliance Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Compliance Change</div>
              <div className="text-2xl font-bold text-gray-600">
                {complianceData.trends.complianceChange === 0 ? 'No Change' : 
                  `${complianceData.trends.complianceChange > 0 ? '+' : ''}${complianceData.trends.complianceChange}%`}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">New Vulnerabilities</div>
              <div className="text-2xl font-bold text-red-600">
                {complianceData.trends.newVulnerabilities}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Resolved Issues</div>
              <div className="text-2xl font-bold text-purple-600">
                {complianceData.trends.resolvedIssues}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}